import { useEffect, useState } from 'react';

import { crmFicheHref } from '../../../../supabase/functions/_shared/crmFiche';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useContactSheet } from '../../../hooks/useContactSheet';
import { newMissionHref } from '../../../lib/bureau/newMission';
import { exchangesOf, openOpportunities } from '../../../lib/crm/accountSheet';
import { contactName, primaryAccountOf, reachOf } from '../../../lib/crm/contactSheet';
import { useCanUseTelephony } from '../../../lib/telephonyAccess';
import { CallButton } from '../../telephony/CallButton';
import { EntityMissionsBlock } from '../EntityMissionsBlock';
import { ExchangesBlock, OpportunitiesBlock } from './AccountSheetBlocks';
import { AccountsBlock, CallsBlock, CoordinatesBlock } from './ContactSheetBlocks';
import { ContactSheetEdit } from './ContactSheetEdit';
import { ContactSheetHeader } from './ContactSheetHeader';
import { EncreSheet, ReReadError, SheetEmpty } from './EncreSheet';

/**
 * A contact's working sheet, in Encre (PLAN-ENCRE-OUTILS O2, mock-up « Encre-Fiche »): who it is and
 * for which account, what Otto does with it and asking him, how to reach it, its accounts, the
 * open deals, the latest exchanges (appointments, notes, meeting reports) and its calls. « Modifier »
 * edits it here — the app's only contact editor. Opened from every name of the app
 * (`openContactSidebar`).
 */
export function ContactSheet({
  contactId,
  startEditing = false,
  onClose,
}: {
  contactId: string | null;
  /** Opened by « Modifier » (Contacts page, full record): straight to the edit form. */
  startEditing?: boolean;
  onClose: () => void;
}) {
  const { openAccountSidebar, openOpportunitySidebar, openAppointmentSidebar, openMeetingReportSidebar } = useSidebar();
  const canCall = useCanUseTelephony();
  const sheet = useContactSheet(contactId);
  const [editing, setEditing] = useState(startEditing);
  const [dirty, setDirty] = useState(false);
  useEffect(() => setEditing(startEditing), [contactId, startEditing]);

  const contact = sheet.contact;
  const ficheHref = contactId ? crmFicheHref('contact', contactId) : '/admin/crm/contacts';
  const rows = { loading: !sheet.rowsReady && !sheet.rowsError, error: sheet.rowsError };

  return (
    <EncreSheet open={contactId !== null} label={contact ? `Fiche de ${contactName(contact)}` : 'Fiche du contact'} focusKey={`${contactId}:${editing}`} dirty={editing && dirty} onClose={onClose}>
      {!contact ? (
        sheet.error ? (
          <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {sheet.error}</p>
        ) : sheet.missing ? (
          <SheetEmpty>Ce contact est introuvable : il a été supprimé, ou n'existe pas.</SheetEmpty>
        ) : (
          <div className="space-y-4 pt-2" aria-busy="true">
            <div className="h-14 w-2/3 animate-pulse rounded-2xl bg-desk-subtle" />
            <div className="h-24 animate-pulse rounded-2xl bg-desk-subtle" />
          </div>
        )
      ) : (
        <>
          {sheet.error && <ReReadError error={sheet.error} />}
          <ContactSheetBody
            contact={contact}
            editing={editing}
            setEditing={setEditing}
            onDirty={setDirty}
            canCall={canCall}
            rows={rows}
            sheet={sheet}
            ficheHref={ficheHref}
            onClose={onClose}
            open={{ account: openAccountSidebar, opportunity: openOpportunitySidebar, appointment: openAppointmentSidebar, report: openMeetingReportSidebar }}
          />
        </>
      )}
    </EncreSheet>
  );
}

function ContactSheetBody({
  contact,
  editing,
  setEditing,
  onDirty,
  canCall,
  rows,
  sheet,
  ficheHref,
  onClose,
  open,
}: {
  contact: NonNullable<ReturnType<typeof useContactSheet>['contact']>;
  editing: boolean;
  setEditing: (next: boolean) => void;
  onDirty: (dirty: boolean) => void;
  canCall: boolean;
  rows: { loading: boolean; error: string | null };
  sheet: ReturnType<typeof useContactSheet>;
  ficheHref: string;
  onClose: () => void;
  open: { account: (id: string) => void; opportunity: (id: string) => void; appointment: (id: string) => void; report: (id: string) => void };
}) {
  const account = primaryAccountOf(contact);
  const { email, phone } = reachOf(contact);
  return (
    <>
      <ContactSheetHeader
        contact={contact}
        account={account}
        email={email}
        call={canCall && phone ? <CallButton number={phone} contactId={contact.id} accountId={account?.id ?? null} /> : null}
        newMissionHref={newMissionHref({ contact: contact.id, client: account?.id ?? null })}
        ficheHref={ficheHref}
        onOpenAccount={open.account}
        onEdit={() => setEditing(true)}
        editing={editing}
        onNavigate={onClose}
      />
      {editing ? (
        <ContactSheetEdit contact={contact} onChanged={sheet.reload} onDone={() => setEditing(false)} onDirty={onDirty} />
      ) : (
        <>
          <EntityMissionsBlock kind="contact" entity={{ id: contact.id, name: contactName(contact) }} account={account} />
          <CoordinatesBlock contact={contact} />
          <AccountsBlock contact={contact} onOpenAccount={open.account} />
          <OpportunitiesBlock opportunities={openOpportunities(sheet.rows.opportunities)} state={rows} onOpen={open.opportunity} />
          <ExchangesBlock
            exchanges={exchangesOf(sheet.rows.appointments, sheet.rows.notes, { reports: sheet.rows.reports })}
            state={rows}
            onOpenAppointment={open.appointment}
            onOpenReport={open.report}
            noteHref={(id) => `/admin/notes?note=${encodeURIComponent(id)}`}
            onNavigate={onClose}
          />
          {canCall && <CallsBlock contactId={contact.id} />}
        </>
      )}
    </>
  );
}
